import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { IdCard, Search, Trash2, ShieldCheck, Building2, FileText } from "lucide-react";
import { toast } from "sonner";

const ROLE_STYLE = {
  admin: "bg-[#2E3A46]/10 text-[#2E3A46] border-[#2E3A46]/30",
  authority: "bg-[#4A7C59]/10 text-[#4A7C59] border-[#4A7C59]/30",
  family: "bg-[#3B6EA5]/10 text-[#3B6EA5] border-[#3B6EA5]/30",
};
const STATUS_STYLE = {
  active: "bg-[#4A7C59]/10 text-[#4A7C59] border-[#4A7C59]/30",
  pending: "bg-[#C99B4B]/15 text-[#8A6A2E] border-[#C99B4B]/40",
  rejected: "bg-[#9E4747]/10 text-[#9E4747] border-[#9E4747]/30",
  suspended: "bg-[#9E4747]/10 text-[#9E4747] border-[#9E4747]/30",
};

export default function AdminAccountsPage() {
  const { user: me } = useAuth();
  const [q, setQ] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback((term) => {
    setLoading(true);
    api.get("/admin/accounts", { params: term ? { q: term } : {} })
      .then((r) => setAccounts(r.data))
      .catch(() => toast.error("Could not load accounts"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const id = setTimeout(() => load(q.trim()), 300);
    return () => clearTimeout(id);
  }, [q, load]);

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      const { data } = await api.delete(`/admin/accounts/${target.id}`);
      toast.success(`Account ${data.email} deleted${data.deleted_cases ? ` (+${data.deleted_cases} case${data.deleted_cases === 1 ? "" : "s"})` : ""}. The email is now free to register.`);
      setTarget(null);
      load(q.trim());
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not delete account");
    }
    setDeleting(false);
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-10" data-testid="admin-accounts-page">
      <p className="font-body text-xs uppercase tracking-[0.18em] text-[#6B7F82]">Admin · account lookup</p>
      <div className="mt-1 flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-[#2E3A46] text-white"><IdCard size={18} /></span>
        <h1 className="font-heading text-3xl font-semibold text-[#1A2024]">All accounts</h1>
      </div>
      <p className="mt-2 max-w-2xl font-body text-sm text-[#5C6873]">
        Search every registered account — including family accounts — by email, name or organisation.
        Use this to find who is already using an address and, if needed, delete the account to free the
        email for a new registration. Only email, role and status are shown; sensitive case data stays private.
      </p>

      <div className="mt-6 max-w-md">
        <div className="relative">
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7F82]" />
          <Input
            placeholder="Search by email, name or organisation…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-11 border-[#E2E8F0] bg-white pl-9"
            data-testid="accounts-search-input"
          />
        </div>
      </div>

      <div className="mt-6 font-body text-sm text-[#5C6873]" data-testid="accounts-count">
        {accounts.length} account{accounts.length === 1 ? "" : "s"}
      </div>

      <div className="mt-3 space-y-2" data-testid="accounts-list">
        {loading && <p className="font-body text-sm text-[#5C6873]" data-testid="accounts-loading">Loading…</p>}
        {!loading && accounts.length === 0 && (
          <div className="rounded-lg border border-dashed border-[#E2E8F0] bg-[#F8F9FA] p-10 text-center font-body text-sm text-[#5C6873]" data-testid="accounts-empty">
            No matching accounts.
          </div>
        )}
        {accounts.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#E2E8F0] bg-white p-4 shadow-sm" data-testid={`account-row-${u.id}`}>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-heading text-base font-semibold text-[#1A2024]" data-testid={`account-name-${u.id}`}>{u.name}</h3>
                <Badge variant="outline" className={`rounded-full text-[11px] capitalize ${ROLE_STYLE[u.role] || ""}`} data-testid={`account-role-${u.id}`}>
                  {u.role === "admin" && <ShieldCheck size={11} className="mr-1" />}{u.role}
                </Badge>
                {u.status && (
                  <Badge variant="outline" className={`rounded-full text-[11px] capitalize ${STATUS_STYLE[u.status] || ""}`} data-testid={`account-status-${u.id}`}>
                    {u.status}
                  </Badge>
                )}
                {u.id === me?.id && (
                  <Badge variant="outline" className="rounded-full border-[#E2E8F0] text-[11px] text-[#6B7F82]">you</Badge>
                )}
              </div>
              <div className="mt-0.5 font-body text-sm text-[#5C6873]" data-testid={`account-email-${u.id}`}>{u.email}</div>
              <div className="font-body text-xs text-[#5C6873]">
                {u.organization ? <span className="inline-flex items-center gap-1"><Building2 size={12} /> {u.organization}</span> : "No organisation"}
                <span className="inline-flex items-center gap-1 ml-2"><FileText size={12} /> {u.case_count} case{u.case_count === 1 ? "" : "s"}</span>
                {u.created_at && <span> · joined {new Date(u.created_at).toLocaleDateString()}</span>}
              </div>
            </div>
            {u.id !== me?.id && (
              <Button
                variant="outline"
                size="sm"
                className="border-[#9E4747]/40 text-[#9E4747] hover:bg-[#9E4747]/10"
                onClick={() => setTarget(u)}
                data-testid={`account-delete-${u.id}`}
              >
                <Trash2 size={14} className="mr-1" /> Delete
              </Button>
            )}
          </div>
        ))}
      </div>

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle>Delete account</DialogTitle>
            <DialogDescription>
              This permanently deletes <span className="font-medium text-[#1A2024]">{target?.email}</span>
              {target?.case_count > 0 && (
                <> and their <span className="font-medium text-[#1A2024]">{target.case_count} submitted case{target.case_count === 1 ? "" : "s"}</span></>
              )}. The email address will become available for a new registration. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setTarget(null)} data-testid="delete-cancel-btn">Cancel</Button>
            <Button
              type="button"
              disabled={deleting}
              onClick={confirmDelete}
              className="bg-[#9E4747] text-white hover:bg-[#8A3D3D]"
              data-testid="delete-confirm-btn"
            >
              {deleting ? "Deleting…" : "Delete account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
